import { createWorker } from 'tesseract.js'

// OCR Worker instance
let worker = null

// Initialize OCR worker
const initOCR = async () => {
  if (!worker) {
    worker = await createWorker('tha+eng')
  }
  return worker
}

// Extract text from image
export const extractTextFromImage = async (file) => {
  try {
    const worker = await initOCR()
    const { data: { text } } = await worker.recognize(file)
    return text
  } catch (error) {
    console.error('OCR Error:', error)
    throw new Error('Failed to extract text from image')
  }
}

// Helper function to clean amount strings by removing commas
const cleanAmountString = (amountStr) => {
  if (!amountStr) return ''
  // Remove commas and other non-numeric characters except decimal point
  return amountStr.replace(/[^\d.]/g, '')
}

// Parse receipt data from extracted text
export const parseReceiptData = (text) => {
  console.log('🔧 Starting receipt parsing...')
  const lines = text.split('\n').filter(line => line.trim())
  console.log('📄 Lines found:', lines.length)
  
  const data = {
    amount: null,
    date: null,
    merchant: null,
    items: [],
    total: null
  }

  // Common patterns for receipt parsing
  const amountPattern = /(\$?\d{1,3}(?:,\d{3})*\.?\d*)|(\d{1,3}(?:,\d{3})*\.?\d*)/g
  const datePattern = /(\d{1,2}[/-]\d{1,2}[/-]\d{2,4})|(\d{4}-\d{2}-\d{2})/
  
  // Thai-specific patterns
  const thaiAmountPattern = /(นวนเงิน *((\d{1,3}(?:,\d{3})*\.\d{2})))/i
  const thaiDatePattern = /(วันที่|วัน).*?(\d{1,2}[/-]\d{1,2}[/-]\d{2,4})/
  const thaiNamePattern = /(น\.ส\.|นาย|ดร\.|คุณ).*?([ก-๙\s]+)/i
  const thaiAddressPattern = /(ไปยัง|ที่อยู่|ส่งไป).*?([ก-๙\s@]+)/i
  const thaiDescriptionPattern = /(\s+([\u0E00-\u0E7F. ]+))/i

  // Extract amounts - try Thai patterns first
  console.log('💰 Looking for Thai amount patterns...')
  const thaiAmountMatch = text.match(thaiAmountPattern)
  console.log('🔍 Thai amount match:', thaiAmountMatch)
  
  if (thaiAmountMatch && thaiAmountMatch[2]) {
    // Remove commas before parsing
    const cleanAmount = cleanAmountString(thaiAmountMatch[2])
    const amount = parseFloat(cleanAmount)
    console.log('💵 Clean amount:', cleanAmount, 'Parsed amount:', amount)
    if (!isNaN(amount) && amount > 0) {
      data.total = amount
      data.amount = amount
      console.log('✅ Amount extracted successfully:', amount)
    }
  }
  
  // Fallback to general amount patterns
  if (!data.amount) {
    console.log('🔄 Trying fallback amount patterns...')
    const amounts = text.match(amountPattern)
    console.log('🔍 General amount matches:', amounts)
    
    if (amounts) {
      // Find the largest amount (likely the total)
      const numericAmounts = amounts
        .map(amt => {
          // Remove commas and other non-numeric characters except decimal point
          const cleanAmount = cleanAmountString(amt)
          return parseFloat(cleanAmount)
        })
        .filter(amt => !isNaN(amt) && amt > 0)
        .sort((a, b) => b - a)
      
      console.log('📊 Numeric amounts found:', numericAmounts)
      
      if (numericAmounts.length > 0) {
        data.total = numericAmounts[0]
        data.amount = numericAmounts[0]
        console.log('✅ Fallback amount extracted:', numericAmounts[0])
      }
    }
  }

  // Extract date - try Thai patterns first
  const thaiDateMatch = text.match(thaiDatePattern)
  if (thaiDateMatch && thaiDateMatch[2]) {
    data.date = thaiDateMatch[2]
  } else {
    // Fallback to general date patterns
    const dateMatch = text.match(datePattern)
    if (dateMatch) {
      data.date = dateMatch[0]
    }
  }

  // Extract merchant name or recipient (usually first few lines)
  console.log('🏪 Looking for merchant/recipient...')
  const firstLines = lines.slice(0, 3)
  console.log('📝 First 3 lines:', firstLines)
  
  for (const line of firstLines) {
    console.log('🔍 Processing line:', line)
    if (line.length > 3 && line.length < 100 && !line.match(amountPattern)) {
      // Skip common Thai receipt headers
      const skipPatterns = /(ใบเสร็จ|RECEIPT|ใบกำกับ|INVOICE|ร้านค้า|STORE)/i
      if (!skipPatterns.test(line)) {
        // Try to extract Thai name/address
        const nameMatch = line.match(thaiNamePattern)
        const addressMatch = line.match(thaiAddressPattern)
        
        console.log('👤 Name match:', nameMatch)
        console.log('📍 Address match:', addressMatch)
        
        if (nameMatch && nameMatch[2]) {
          data.merchant = nameMatch[2].trim()
          console.log('✅ Name extracted:', data.merchant)
        } else if (addressMatch && addressMatch[2]) {
          data.merchant = addressMatch[2].trim()
          console.log('✅ Address extracted:', data.merchant)
        } else {
          data.merchant = line.trim()
          console.log('✅ Line used as merchant:', data.merchant)
        }
        break
      } else {
        console.log('⏭️ Skipping header line:', line)
      }
    } else {
      console.log('⏭️ Skipping line (too short/long or contains amount):', line)
    }
  }

  // Extract description from all lines
  console.log('📝 Looking for description...')
  let descriptionFound = false
  
  for (const line of lines) {
    if (descriptionFound) break
    
    console.log('🔍 Checking line for description:', line)
    
    // Skip lines that are too short or contain amounts
    if (line.length < 5 || line.match(amountPattern)) {
      console.log('⏭️ Skipping line (too short or contains amount):', line)
      continue
    }
    
    // Try to extract Thai description patterns
    const descMatch = line.match(thaiDescriptionPattern)
    if (descMatch && descMatch[2]) {
      data.merchant = descMatch[2].trim()
      console.log('✅ Description extracted:', data.merchant)
      descriptionFound = true
      break
    }
    
    // If no specific pattern, use meaningful lines
    const skipPatterns = /(ใบเสร็จ|RECEIPT|ใบกำกับ|INVOICE|ร้านค้า|STORE|จำนวนเงิน|ยอดรวม|รวม|ราคา)/i
    if (!skipPatterns.test(line) && line.length > 5 && line.length < 200) {
      // Check if line contains Thai characters
      const thaiCharPattern = /[ก-๙]/
      if (thaiCharPattern.test(line)) {
        data.merchant = line.trim()
        console.log('✅ Thai description found:', data.merchant)
        descriptionFound = true
        break
      }
    }
  }

  // Extract items (lines with amounts)
  lines.forEach(line => {
    const amountMatch = line.match(amountPattern)
    if (amountMatch && line.length > 5) {
      const itemName = line.replace(amountPattern, '').trim()
      if (itemName.length > 2) {
        data.items.push({
          name: itemName,
          amount: parseFloat(amountMatch[0].replace('$', ''))
        })
      }
    }
  })

  // Clean up Thai names and addresses
  if (data.merchant) {
    console.log('🧹 Cleaning up merchant name:', data.merchant)
    // Remove common prefixes and clean up
    data.merchant = data.merchant
      .replace(/^(น\.ส\.|นาย|ดร\.|คุณ)\s*/i, '') // Remove title prefixes
      .replace(/^ไปยัง\s*/i, '') // Remove "ไปยัง" prefix
      .replace(/@\s*/g, '') // Remove @ symbols
      .trim()
    console.log('✨ Cleaned merchant name:', data.merchant)
    
    // Check if description is too short and set to "no name"
    if (data.merchant.length < 5) {
      console.log('⚠️ Description too short (< 5 chars), setting to "no name"')
      data.merchant = 'no name'
    }
  }
  
  console.log('🎉 Final parsed data:', data)
  return data
}

// Clean up worker
export const cleanupOCR = async () => {
  if (worker) {
    await worker.terminate()
    worker = null
  }
}

// Main function to process receipt
export const processReceipt = async (file) => {
  try {
    console.log('🔍 Starting OCR processing...')
    const text = await extractTextFromImage(file)
    console.log('📝 Raw OCR Text:', text)
    
    const parsedData = parseReceiptData(text)
    console.log('🎯 Parsed Data:', parsedData)
    
    return {
      success: true,
      rawText: text, // Show raw OCR text
      text: text,
      parsedData
    }
  } catch (error) {
    console.error('❌ OCR Error:', error)
    return {
      success: false,
      error: error.message,
      rawText: '',
      text: '',
      parsedData: {}
    }
  }
} 